import React, { useState, useRef, useCallback, useEffect, useMemo, useContext } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSpinner,
  faMapMarkerAlt,
  faGlobe,
  faPhone,
  faEnvelope,
  faStar,
  faBuilding,
  faMagic,
  faExclamationTriangle,
  faCopy,
  faCheck,
  faPhoneAlt,
  faIdCard,
  faPaperPlane,
  faEdit,
  faTrash,
  faRocket,
  faListAlt,
  faInfoCircle,
  faExternalLinkAlt,
  faSync,
  faBookOpen,
} from "@fortawesome/free-solid-svg-icons";

import OnboardingService, {
  IPlaceSearchResult,
  IPlaceDetails,
  IEnrichmentResult,
  IYtjSearchResult,
  IYtjDetails,
  IGeneratedEmail,
  IOutreachBatch,
} from "../../services/OnboardingService";
import LeadService, { ILead, ILeadNote, LEAD_STATUS_LABELS, LeadStatus } from "../../services/LeadService";
import { SupplyPipelineTab } from "./SupplyPipelineTab";
import { KnowledgeBaseTab } from "./KnowledgeBaseTab";
import { IAppState, IAuthState } from "../../interfaces";
import { IOption } from "../../components/UI/Input/Input";
import Select from "../../components/UI/Select/Select";
import ModalContext, { EModalSize } from "../../components/UI/Modal/ModalContext";
import ConfirmDialog from "../../components/UI/ConfirmDialog/ConfirmDialog";
import classes from "./Onboarding.module.scss";
import tolCodesData from "../../data/tolCodes.json";
import postCodesData from "../../data/finnishPostCodes.json";
import finnishCities from "../../data/finnishCities.json";

interface IProps extends RouteComponentProps {}

const Onboarding: React.FC<IProps> = () => {
  const { setModal, closeModal } = useContext(ModalContext);

  // ---- Restore persisted search state ----
  const savedSearch = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("onb_search") || "{}"); }
    catch { return {}; }
  }, []);

  // ---- Search mode ----
  const [searchMode, setSearchMode] = useState<"places" | "ytjTol">(
    savedSearch.mode || "places"
  );

  // ---- Column 1: Places Search ----
  const [searchQuery, setSearchQuery] = useState(savedSearch.placesQuery || "");
  const [searchCity, setSearchCity] = useState(savedSearch.placesCity || "");
  const [searchResults, setSearchResults] = useState<IPlaceSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // ---- Column 1: YTJ TOL Search ----
  const [selectedTolCode, setSelectedTolCode] = useState(savedSearch.tolCode || "");
  const [tolNameQuery, setTolNameQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(savedSearch.location || "");
  const [selectedPostCode, setSelectedPostCode] = useState(savedSearch.postCode || "");
  const [tolResults, setTolResults] = useState<IYtjSearchResult[]>([]);
  const [tolTotalResults, setTolTotalResults] = useState(0);
  const [tolPage, setTolPage] = useState(1);
  const [tolSearching, setTolSearching] = useState(false);
  const [tolError, setTolError] = useState<string | null>(null);
  const [tolResultFilter, setTolResultFilter] = useState("");

  // ---- Outreach Batch ----
  const [outreachBatches, setOutreachBatches] = useState<IOutreachBatch[]>([]);
  const [batchStarting, setBatchStarting] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);
  const batchPollRef = useRef<NodeJS.Timeout | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);
  const [batchLeads, setBatchLeads] = useState<any[]>([]);
  const [batchLeadsLoading, setBatchLeadsLoading] = useState(false);

  // ---- Admin Tab selection (haku vs pipelines vs kb) ----
  const [onbTab, setOnbTab] = useState<"haku" | "pipelines" | "kb">("haku");
  const [activeKbToken, setActiveKbToken] = useState<string>("");

  // ---- Persist search values to localStorage as single JSON ----
  useEffect(() => {
    localStorage.setItem("onb_search", JSON.stringify({
      mode: searchMode,
      placesQuery: searchQuery,
      placesCity: searchCity,
      tolCode: selectedTolCode,
      location: selectedLocation,
      postCode: selectedPostCode,
    }));
  }, [searchMode, searchQuery, searchCity, selectedTolCode, selectedLocation, selectedPostCode]);

  // ---- Switch mode handler: clears the other mode's state ----
  const switchMode = useCallback((mode: "places" | "ytjTol") => {
    setSearchMode(mode);
    if (mode === "places") {
      setSelectedTolCode("");
      setSelectedLocation("");
      setSelectedPostCode("");
      setTolResults([]);
      setTolTotalResults(0);
      setTolResultFilter("");
      setTolNameQuery("");
    } else {
      setSearchQuery("");
      setSearchCity("");
      setSearchResults([]);
      setSearchError(null);
    }
  }, []);

  // ---- Auto-search Places on mount if saved query ----
  const initialSearchDone = useRef(false);
  useEffect(() => {
    const fullQuery = `${searchQuery} ${searchCity}`.trim();
    if (!initialSearchDone.current && searchMode === "places" && fullQuery.length >= 2) {
      initialSearchDone.current = true;
      setSearching(true);
      OnboardingService.searchPlaces(fullQuery)
        .then(setSearchResults)
        .catch(() => {})
        .finally(() => setSearching(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized options for react-select
  const tolOptions: IOption[] = useMemo(() => {
    const base = tolCodesData.map(t => ({ value: t.code, label: `${t.code} ${t.name}` }));
    // Include persisted custom code if not in the base list
    if (selectedTolCode && !base.some(o => o.value === selectedTolCode)) {
      base.unshift({ value: selectedTolCode, label: selectedTolCode });
    }
    return base;
  }, [selectedTolCode]);
  const cityOptions: IOption[] = useMemo(() => {
    const base = finnishCities.map((c: { value: string; label: string }) => ({ value: c.value, label: c.label }));
    if (selectedLocation && !base.some(o => o.value === selectedLocation)) {
      base.unshift({ value: selectedLocation, label: selectedLocation });
    }
    return base;
  }, [selectedLocation]);
  const postCodeOptions: IOption[] = useMemo(() => {
    const base = postCodesData.map(p => ({ value: p.value, label: p.label }));
    if (selectedPostCode && !base.some(o => o.value === selectedPostCode)) {
      base.unshift({ value: selectedPostCode, label: selectedPostCode });
    }
    return base;
  }, [selectedPostCode]);

  // ---- Column 2: Details ----
  const [placeDetails, setPlaceDetails] = useState<IPlaceDetails | null>(null);
  const [loadingPlace, setLoadingPlace] = useState(false);
  const [enrichment, setEnrichment] = useState<IEnrichmentResult | null>(null);
  const [loadingEnrichment, setLoadingEnrichment] = useState(false);
  const [ytjResults, setYtjResults] = useState<IYtjSearchResult[]>([]);
  const [ytjDetails, setYtjDetails] = useState<IYtjDetails | null>(null);
  const [loadingYtj, setLoadingYtj] = useState(false);
  const [ytjManualInput, setYtjManualInput] = useState("");
  const [selectedYtjId, setSelectedYtjId] = useState<string | null>(null);

  // ---- Column 3: Contact ----
  const [contactStatus, setContactStatus] = useState<string>("NOT_CONTACTED");
  const [contactMethod, setContactMethod] = useState<string>("PHONE");
  const [generatedEmail, setGeneratedEmail] = useState<IGeneratedEmail | null>(null);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [copied, setCopied] = useState(false);

  // ---- Lead save state ----
  const [savingLead, setSavingLead] = useState(false);
  const [savedLead, setSavedLead] = useState<ILead | null>(null);
  const [duplicateLead, setDuplicateLead] = useState<ILead | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [updatingLead, setUpdatingLead] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const selectionSeqRef = useRef<number>(0);

  // ---- Notes state (OfferNote-style) ----
  const [notesList, setNotesList] = useState<ILeadNote[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // Derived state
  const selectedPlaceName = placeDetails?.name || searchResults.find((r) => r.placeId === selectedPlaceId)?.name || "";

  const isToiminimi = useMemo(() => {
    // 1. Authoritative YTJ details take highest precedence
    if (ytjDetails) return !!ytjDetails.isToiminimi;

    // 2. While loading company details, place or YTJ, never assume Toiminimi (prevents UI flicker)
    if (initialLoading || loadingPlace || loadingYtj || loadingEnrichment || (selectedYtjId !== null && !ytjDetails)) {
      return false;
    }

    // 3. Saved or duplicate lead
    if (savedLead) return !!savedLead.isToiminimi;
    if (duplicateLead) return !!duplicateLead.isToiminimi;

    // 4. Name heuristic: Suffixes like Oy, Ab, Ky, Ay are never Toiminimi
    const nameToCheck = placeDetails?.name || selectedPlaceName;
    if (nameToCheck && /\b(oy|oyj|ab|aktiebolag|osakeyhtiö|ky|kommandiittiyhtiö|ay|avoin yhtiö)\b/i.test(nameToCheck)) {
      return false;
    }

    // 5. Fallback: Assume Tmi ONLY after all searches/loading have completed with 0 results, but AI found a business ID (government open API restriction on sole traders)
    if (enrichment?.data?.businessId && ytjResults.length === 0) {
      return true;
    }

    return false;
  }, [
    ytjDetails,
    initialLoading,
    loadingPlace,
    loadingYtj,
    loadingEnrichment,
    selectedYtjId,
    savedLead,
    duplicateLead,
    placeDetails,
    selectedPlaceName,
    enrichment,
    ytjResults,
  ]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const [deletingNote, setDeletingNote] = useState(false);

  const { authUser } = useSelector<IAppState, IAuthState>((state) => state.auth);

  // ---- Search handler with debounce ----
  const handleSearchChange = useCallback((value: string, city: string = searchCity) => {
    setSearchQuery(value);
    setSearchError(null);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    const fullQuery = `${value} ${city}`.trim();
    if (fullQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await OnboardingService.searchPlaces(fullQuery);
        setSearchResults(results);
      } catch (err: any) {
        setSearchError("Haku epäonnistui");
        console.error("Places search error:", err);
      } finally {
        setSearching(false);
      }
    }, 500);
  }, [searchCity]);

  const handleCityChange = useCallback((city: string) => {
    setSearchCity(city);
    handleSearchChange(searchQuery, city);
  }, [searchQuery, handleSearchChange]);

  // ---- TOL Search handler ----
  const handleTolSearch = useCallback(async (page: number) => {
    if (!selectedTolCode && !tolNameQuery) return;
    setTolSearching(true);
    setTolError(null);
    if (page === 1) setTolResultFilter("");
    setTolPage(page);
    try {
      const resp = await OnboardingService.searchYtj({
        name: tolNameQuery || undefined,
        mainBusinessLine: selectedTolCode || undefined,
        location: selectedLocation || undefined,
        postCode: selectedPostCode || undefined,
        page,
      });
      setTolResults(resp.companies || []);
      setTolTotalResults(resp.totalResults || 0);
    } catch (err: any) {
      setTolError("YTJ-haku epäonnistui");
      console.error("TOL search error:", err);
    } finally {
      setTolSearching(false);
    }
  }, [selectedTolCode, tolNameQuery, selectedLocation, selectedPostCode]);

  // ---- Outreach Batch handlers ----
  const loadBatches = useCallback(async () => {
    try {
      const batches = await OnboardingService.listOutreachBatches();
      setOutreachBatches(batches);
    } catch (err) {
      console.error("Failed to load batches", err);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // Poll active batches
  useEffect(() => {
    const hasActive = outreachBatches.some(b => b.status === 'PENDING' || b.status === 'IN_PROGRESS');
    if (hasActive) {
      batchPollRef.current = setInterval(loadBatches, 3000);
    } else if (batchPollRef.current) {
      clearInterval(batchPollRef.current);
      batchPollRef.current = null;
    }
    return () => { if (batchPollRef.current) clearInterval(batchPollRef.current); };
  }, [outreachBatches, loadBatches]);

  const handleStartBatch = useCallback(async () => {
    if (!selectedTolCode) return;
    const tolEntry = tolCodesData.find(t => t.code === selectedTolCode);
    const tolLabel = tolEntry ? `${tolEntry.code} ${tolEntry.name}` : selectedTolCode;
    const areaLabel = [selectedPostCode, selectedLocation].filter(Boolean).join(", ") || "koko Suomi";

    const doStart = async () => {
      closeModal();
      setBatchStarting(true);
      setBatchError(null);
      try {
        await OnboardingService.startOutreachBatch({
          mainBusinessLine: selectedTolCode,
          mainBusinessLineName: tolEntry?.name || selectedTolCode,
          postCode: selectedPostCode || undefined,
          city: selectedLocation || undefined,
        });
        await loadBatches();
      } catch (err: any) {
        setBatchError(err?.response?.data?.message || "Taustahaun käynnistys epäonnistui");
        console.error("Start batch error:", err);
      } finally {
        setBatchStarting(false);
      }
    };

    setModal({
      isOpen: true,
      title: "Vahvista taustahaku",
      size: EModalSize.SMALL,
      content: (
        <ConfirmDialog
          body={
            <div>
              <p style={{ margin: '0 0 12px' }}>
                Haetaan kaikki yritykset YTJ:stä ja tallennetaan liideinä:
              </p>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', fontSize: '0.9rem' }}>
                <div><strong>Toimiala:</strong> {tolLabel}</div>
                <div><strong>Alue:</strong> {areaLabel}</div>
                {tolTotalResults > 0 && (
                  <div style={{ marginTop: '6px', borderTop: '1px solid #e2e8f0', paddingTop: '6px' }}>
                    <strong>Arvioitu määrä:</strong>{' '}
                    <span style={{ color: '#2563eb', fontWeight: 600 }}>{tolTotalResults} yritystä</span>
                  </div>
                )}
              </div>
              <p style={{ margin: '12px 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                Duplikaatit ohitetaan automaattisesti. Mahdolliset virheet näkyvät taustahaun tiedoissa.
              </p>
            </div>
          }
          onConfirm={doStart}
          onCancel={closeModal}
        />
      ),
    });
  }, [selectedTolCode, selectedPostCode, selectedLocation, tolTotalResults, loadBatches, setModal, closeModal]);

  // ---- Fetch Notes helper ----
  const fetchNotes = useCallback(async (leadId: string) => {
    try {
      setNotesLoading(true);
      const notes = await LeadService.listLeadNotes(leadId);
      setNotesList(notes);
    } catch (err) {
      console.error("Error fetching notes:", err);
    } finally {
      setNotesLoading(false);
    }
  }, []);

  // ---- Select a YTJ result ----
  const handleSelectYtj = useCallback(async (businessId: string, expectedSeq?: number) => {
    let seq = expectedSeq;
    if (seq === undefined) {
      seq = ++selectionSeqRef.current;
    } else if (selectionSeqRef.current !== seq) {
      return;
    }

    setSelectedYtjId(businessId);
    setSavedLead(null);
    setDuplicateLead(null);
    setNotesList([]);
    setNewNoteText("");

    // Check for existing lead by businessId first
    setCheckingDuplicate(true);
    let existingLead: any = null;
    try {
      const dupResult = await LeadService.checkDuplicate(undefined, businessId);
      if (selectionSeqRef.current !== seq) return;
      if (dupResult.isDuplicate && dupResult.existingLead) {
        existingLead = dupResult.existingLead;
        setDuplicateLead(dupResult.existingLead);
        setSavedLead(dupResult.existingLead);
        setContactStatus(dupResult.existingLead.status || "NOT_CONTACTED");
        setContactMethod(dupResult.existingLead.contactMethod || "PHONE");
        fetchNotes(dupResult.existingLead.id);
      }
    } catch (err) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Duplicate check error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setCheckingDuplicate(false);
      }
    }

    // Fetch YTJ details
    setLoadingYtj(true);
    try {
      const details = await OnboardingService.getYtjDetails(businessId);
      if (selectionSeqRef.current !== seq) return;
      setYtjDetails(details);
      // Auto-lock email for Tmi
      if (details?.isToiminimi) {
        setContactMethod("PHONE");
      }
      // If we have an existing lead whose isToiminimi or companyForm was incorrect, update it
      if (existingLead?.id && details && (existingLead.isToiminimi !== !!details.isToiminimi || !existingLead.companyForm)) {
        LeadService.updateLead(existingLead.id, {
          isToiminimi: !!details.isToiminimi,
          companyForm: details.companyForm,
          ytjName: details.name,
        }).then((updated) => {
          if (selectionSeqRef.current === seq) {
            setSavedLead(updated);
            setDuplicateLead(updated);
          }
        }).catch((err) => console.error("Self-heal existing lead error:", err));
      }
    } catch (err: any) {
      if (selectionSeqRef.current !== seq) return;
      console.error("YTJ details error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setLoadingYtj(false);
      }
    }
  }, [fetchNotes]);

  // ---- Vertex AI Enrichment ----
  const handleEnrich = useCallback(async (
    force: boolean = false,
    overrideDetails?: IPlaceDetails,
    overrideExistingLead?: ILead | null,
    expectedSeq?: number,
    overrideName?: string
  ) => {
    const seq = expectedSeq !== undefined ? expectedSeq : selectionSeqRef.current;
    if (selectionSeqRef.current !== seq) return;

    const details = overrideDetails || placeDetails;
    const name = overrideName || details?.name || selectedPlaceName || ytjDetails?.name;
    if (!name) return;

    // 1. Check if we already have this in the lead and not forcing
    // Explicit overrideExistingLead takes precedence over stale closure state
    const existing = overrideExistingLead !== undefined ? overrideExistingLead : (savedLead || duplicateLead);
    if (!force && existing && (existing.email || existing.contactPerson || existing.description)) {
      if (selectionSeqRef.current !== seq) return;
      setEnrichment({
        data: {
          email: existing.email,
          contactPerson: existing.contactPerson,
          description: existing.description,
          phone: existing.phone,
          website: existing.website,
          businessId: existing.businessId,
          city: existing.city,
          address: existing.street,
          postCode: existing.postCode,
        },
        sources: [], // Sources not persisted currently
      });
      return;
    }

    // 2. Otherwise fetch from AI
    setLoadingEnrichment(true);
    try {
      const result = await OnboardingService.enrichCompany({
        companyName: name,
        website: details?.website || ytjDetails?.website || undefined,
        city: details?.address ? extractCityFromDetails(details.address) : ytjDetails?.addresses?.[0]?.city || undefined,
      });
      if (selectionSeqRef.current !== seq) return;
      setEnrichment(result);

      // 3. If forcing and we have a saved lead, update it
      const targetLeadId = existing?.id || savedLead?.id;
      if (force && targetLeadId) {
        const updated = await LeadService.updateLead(targetLeadId, {
          email: result.data?.email || undefined,
          contactPerson: result.data?.contactPerson || undefined,
          phone: result.data?.phone || savedLead?.phone,
          website: result.data?.website || savedLead?.website,
          street: result.data?.address || savedLead?.street,
          postCode: result.data?.postCode || savedLead?.postCode,
          city: result.data?.city || savedLead?.city,
          description: result.data?.description || savedLead?.description,
        });
        if (selectionSeqRef.current === seq) {
          setSavedLead(updated);
        }
      }
    } catch (err) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Enrichment error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setLoadingEnrichment(false);
      }
    }
  }, [placeDetails, selectedPlaceName, ytjDetails, savedLead, duplicateLead]);

  // ---- Select a YTJ TOL result → load details into Column 2 ----
  const handleSelectYtjFromTol = useCallback(async (result: IYtjSearchResult) => {
    const seq = ++selectionSeqRef.current;

    setSelectedPlaceId(null);
    setPlaceDetails(null);
    setEnrichment(null);
    setSelectedYtjId(result.businessId);
    setYtjResults([result]);
    setYtjDetails(null);
    setGeneratedEmail(null);
    setContactStatus("NOT_CONTACTED");
    setContactMethod("PHONE");
    setSavedLead(null);
    setDuplicateLead(null);
    setNotesList([]);
    setNewNoteText("");

    // Check for duplicate by businessId
    let existingLead: any = null;
    setCheckingDuplicate(true);
    try {
      const dupResult = await LeadService.checkDuplicate(undefined, result.businessId);
      if (selectionSeqRef.current !== seq) return;
      if (dupResult.isDuplicate && dupResult.existingLead) {
        existingLead = dupResult.existingLead;
        setDuplicateLead(dupResult.existingLead);
        setSavedLead(dupResult.existingLead);
        setContactStatus(dupResult.existingLead.status || "NOT_CONTACTED");
        setContactMethod(dupResult.existingLead.contactMethod || "PHONE");
        fetchNotes(dupResult.existingLead.id);
      }
    } catch (err) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Duplicate check error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setCheckingDuplicate(false);
      }
    }

    // Load YTJ details
    handleSelectYtj(result.businessId, seq);

    // Enrich: search Google Places by company name (display only, not persisted)
    if (result.name) {
      const city = result.city || undefined;
      setLoadingPlace(true);
      OnboardingService.searchPlaces(`${result.name} ${city || ""}`.trim())
        .then((places) => {
          if (selectionSeqRef.current !== seq) return;
          if (places.length > 0) {
            // Auto-fetch first place details for enrichment display + save placeId to lead
            const matchedPlaceId = places[0].placeId;
            setSelectedPlaceId(matchedPlaceId);
            return OnboardingService.getPlaceDetails(matchedPlaceId)
              .then((details) => {
                if (selectionSeqRef.current === seq) {
                  setPlaceDetails(details);
                }
              })
              .catch((err) => console.error("Places enrichment error:", err));
          }
        })
        .catch((err) => {
          if (selectionSeqRef.current !== seq) return;
          console.error("Places search for enrichment error:", err);
        })
        .finally(() => {
          if (selectionSeqRef.current === seq) {
            setLoadingPlace(false);
          }
        });

      // AI enrichment via unified handleEnrich
      handleEnrich(false, undefined, existingLead, seq, result.name);
    }
  }, [fetchNotes, handleSelectYtj, handleEnrich]);

  // ---- Select a place result ----
  const handleSelectPlace = useCallback(async (placeId: string) => {
    const seq = ++selectionSeqRef.current;

    setSelectedPlaceId(placeId);
    setPlaceDetails(null);
    setEnrichment(null);
    setYtjResults([]);
    setYtjDetails(null);
    setSelectedYtjId(null);
    setGeneratedEmail(null);
    setContactStatus("NOT_CONTACTED");
    setContactMethod("PHONE");
    setSavedLead(null);
    setDuplicateLead(null);
    setNotesList([]);
    setNewNoteText("");
    setYtjManualInput("");
    setInitialLoading(true);
    setLoadingPlace(true);
    setLoadingYtj(true);
    setLoadingEnrichment(true);

    // 0. Check for duplicate — if exists, load it and its notes
    let existingLead: ILead | null = null;
    setCheckingDuplicate(true);
    try {
      const result = await LeadService.checkDuplicate(placeId);
      if (selectionSeqRef.current !== seq) return;
      if (result.isDuplicate && result.existingLead) {
        existingLead = result.existingLead;
        setDuplicateLead(result.existingLead);
        setSavedLead(result.existingLead);
        setContactStatus(result.existingLead.status || "NOT_CONTACTED");
        setContactMethod(result.existingLead.contactMethod || "PHONE");
        // Load notes for existing lead
        fetchNotes(result.existingLead.id);
      } else {
        setDuplicateLead(null);
      }
    } catch (err) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Duplicate check error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setCheckingDuplicate(false);
      }
    }

    // 1. Fetch Place Details
    try {
      const details = await OnboardingService.getPlaceDetails(placeId);
      if (selectionSeqRef.current !== seq) return;
      setPlaceDetails(details);

      // 2. Trigger enrichment with fresh details & explicit existingLead (avoids stale closure)
      handleEnrich(false, details, existingLead, seq);

      // 3. Trigger YTJ search (async, non-blocking)
      setLoadingYtj(true);
      OnboardingService.searchYtj({ name: details.name, location: extractCityFromDetails(details.address) })
        .then((resp) => {
          if (selectionSeqRef.current !== seq) return;
          setYtjResults(resp.companies || []);
          // Auto-select first result
          if (resp.companies && resp.companies.length === 1) {
            return handleSelectYtj(resp.companies[0].businessId, seq);
          }
        })
        .catch((err) => {
          if (selectionSeqRef.current !== seq) return;
          console.error("YTJ search error:", err);
        })
        .finally(() => {
          if (selectionSeqRef.current === seq) {
            setLoadingYtj(false);
          }
        });
    } catch (err: any) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Place details error:", err);
      setLoadingYtj(false);
      setLoadingEnrichment(false);
    } finally {
      if (selectionSeqRef.current === seq) {
        setLoadingPlace(false);
        setInitialLoading(false);
      }
    }
  }, [fetchNotes, handleSelectYtj, handleEnrich]);

  const handleSelectLeadFromPipeline = useCallback((lead: any) => {
    setOnbTab("haku");
    if (lead.placeId) {
      handleSelectPlace(lead.placeId);
    } else if (lead.businessId) {
      handleSelectYtj(lead.businessId);
    }
  }, [handleSelectPlace, handleSelectYtj]);

  // ---- Manual YTJ search by Y-tunnus ----
  const handleManualYtjSearch = useCallback(async () => {
    const input = ytjManualInput.trim();
    if (!input) return;

    const seq = ++selectionSeqRef.current;
    setLoadingYtj(true);
    setYtjResults([]);
    setYtjDetails(null);
    try {
      // Check if input looks like a Business ID (e.g. 1234567-8 or 12345678)
      const isBusinessId = /^\d{7}-?\d$/.test(input);

      if (isBusinessId) {
        const details = await OnboardingService.getYtjDetails(input);
        if (selectionSeqRef.current !== seq) return;
        if (details) {
          setYtjDetails(details);
          setSelectedYtjId(details.businessId);
        }
      } else {
        // Search by name
        const resp = await OnboardingService.searchYtj({ name: input });
        if (selectionSeqRef.current !== seq) return;
        if (resp.companies && resp.companies.length === 1) {
          // Only one match, load it directly
          handleSelectYtj(resp.companies[0].businessId, seq);
        } else {
          // Multiple results or none, show the list (UI already handles ytjResults.length > 1)
          setYtjResults(resp.companies || []);
        }
      }
    } catch (err: any) {
      if (selectionSeqRef.current !== seq) return;
      console.error("Manual YTJ error:", err);
    } finally {
      if (selectionSeqRef.current === seq) {
        setLoadingYtj(false);
      }
    }
  }, [ytjManualInput, handleSelectYtj]);

  // ---- Generate email ----
  const handleGenerateEmail = useCallback(async () => {
    setGeneratingEmail(true);
    setGeneratedEmail(null);
    try {
      const result = await OnboardingService.generateEmail({
        companyName: placeDetails?.name || selectedPlaceName,
        contactName: enrichment?.data?.contactPerson || undefined,
        companyForm: ytjDetails?.companyForm || undefined,
        city: placeDetails?.address ? extractCityFromDetails(placeDetails.address) : undefined,
        website: placeDetails?.website || enrichment?.data?.website || undefined,
        businessType: ytjDetails?.businessLines?.[0]?.name || placeDetails?.primaryType || undefined,
      });
      setGeneratedEmail(result);
    } catch (err: any) {
      console.error("Email generation error:", err);
    } finally {
      setGeneratingEmail(false);
    }
  }, [placeDetails, enrichment, ytjDetails, selectedPlaceName]);

  // ---- Copy email to clipboard ----
  const handleCopyEmail = useCallback(() => {
    if (!generatedEmail) return;
    const text = `Aihe: ${generatedEmail.subject}\n\n${generatedEmail.body}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [generatedEmail]);

  // ---- Helpers ----
  function extractCityFromDetails(address: string | undefined): string | undefined {
    if (!address) return undefined;
    const parts = address.split(",");
    if (parts.length >= 2) {
      const candidate = parts[parts.length >= 3 ? parts.length - 2 : parts.length - 1].trim();
      return candidate.replace(/^\d{5}\s*/, "") || undefined;
    }
    return undefined;
  }



  // ---- Manual YTJ search by Y-tunnus ----
  const formatRelativeTime = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHr = Math.floor(diffMin / 60);
      const diffDays = Math.floor(diffHr / 24);
      if (diffSec < 60) return 'juuri nyt';
      if (diffMin < 60) return `${diffMin} min sitten`;
      if (diffHr < 24) return `${diffHr} t sitten`;
      if (diffDays === 1) return 'eilen';
      if (diffDays < 7) return `${diffDays} pv sitten`;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}.${date.getFullYear()}`;
    } catch { return ''; }
  };

  const getInitials = (name?: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };



  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim() || !savedLead?.id) return;
    try {
      setSavingNote(true);
      const note = await LeadService.addLeadNote(savedLead.id, newNoteText);
      setNotesList(prev => [note, ...prev]);
      setNewNoteText("");
      setIsComposerFocused(false);
    } catch (err) {
      console.error("Error adding note:", err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleEditNote = async (id: string, text: string) => {
    if (!text.trim()) return;
    try {
      setSavingEdit(true);
      const updated = await LeadService.updateLeadNote(id, text);
      setNotesList(prev => prev.map(n => n.id === id ? updated : n));
      setEditingNoteId(null);
      setEditingNoteText("");
    } catch (err) {
      console.error("Error editing note:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteNote = async (id: string) => {
    try {
      setDeletingNote(true);
      await LeadService.deleteLeadNote(id);
      setNotesList(prev => prev.filter(n => n.id !== id));
      setDeletingNoteId(null);
    } catch (err) {
      console.error("Error deleting note:", err);
    } finally {
      setDeletingNote(false);
    }
  };

  // ---- Auto-save lead on place/ytj select (collects available data) ----
  const autoSaveLead = useCallback(async () => {
    const businessId = ytjDetails?.businessId || enrichment?.data?.businessId;
    if (!businessId || savedLead || duplicateLead) return;
    const seq = selectionSeqRef.current;

    setSavingLead(true);
    try {
      const ytjAddress = ytjDetails?.addresses?.find(a => a.type === 1) || ytjDetails?.addresses?.[0];
      const isTmi = ytjDetails ? !!ytjDetails.isToiminimi : (!loadingYtj && !selectedYtjId && !!enrichment?.data?.businessId);

      const saved = await LeadService.saveLead({
        placeId: selectedPlaceId || undefined,
        placeName: ytjDetails?.name || selectedPlaceName || undefined,
        businessId: businessId,
        ytjName: ytjDetails?.name || selectedPlaceName || undefined,
        companyForm: ytjDetails?.companyForm || (!ytjDetails && enrichment?.data?.businessId ? "Tmi (AI)" : undefined),
        businessLine: ytjDetails?.businessLines?.[0]?.name,
        street: ytjAddress?.street || enrichment?.data?.address || undefined,
        postCode: ytjAddress?.postCode || enrichment?.data?.postCode || undefined,
        city: ytjAddress?.city || enrichment?.data?.city || undefined,
        // GDPR & Google ToS: tallennetaan vain julkisesta YTJ-rekisteristä saadut tiedot tai AI:n internetistä löytämä data.
        // Google Places -dataa ei tallenneta suoraan (ToS kielto tallentaa muuta kuin Place ID).
        phone: ytjDetails?.phone || enrichment?.data?.phone || undefined,
        website: ytjDetails?.website || enrichment?.data?.website || undefined,
        email: enrichment?.data?.email || undefined,
        contactPerson: enrichment?.data?.contactPerson || undefined,
        description: enrichment?.data?.description || undefined,
        status: contactStatus,
        contactMethod: contactMethod,
        generatedEmailSubject: generatedEmail?.subject,
        generatedEmailBody: generatedEmail?.body,
        isToiminimi: isTmi,
      });
      if (selectionSeqRef.current === seq) {
        setSavedLead(saved);
        setDuplicateLead(null);
      }
    } catch (err: any) {
      if (selectionSeqRef.current === seq) {
        console.error("Auto-save lead error:", err);
      }
    } finally {
      if (selectionSeqRef.current === seq) {
        setSavingLead(false);
      }
    }
  }, [selectedPlaceId, selectedPlaceName, ytjDetails, loadingYtj, selectedYtjId, enrichment, contactStatus, contactMethod, generatedEmail, savedLead, duplicateLead]);

  // Auto-save when YTJ details are loaded or AI finds a Business ID and YTJ search is not running
  useEffect(() => {
    const hasBusinessId = ytjDetails || (enrichment?.data?.businessId && !loadingYtj && !selectedYtjId);
    if (hasBusinessId && !savedLead && !duplicateLead && !savingLead && !checkingDuplicate) {
      autoSaveLead();
    }
  }, [ytjDetails, enrichment, loadingYtj, selectedYtjId, savedLead, duplicateLead, savingLead, checkingDuplicate, autoSaveLead]);

  // Auto-fetch YTJ details if AI found a Business ID and we don't have a match yet
  useEffect(() => {
    // Condition: AI found ID, we don't have details yet, we are not searching YTJ, and no name-search results found
    if (enrichment?.data?.businessId && !ytjDetails && !loadingYtj && !selectedYtjId) {
      handleSelectYtj(enrichment.data.businessId, selectionSeqRef.current);
    }
  }, [enrichment, ytjDetails, loadingYtj, ytjResults, selectedYtjId, handleSelectYtj]);

  // ---- Auto-update on status/method change ----
  const handleStatusChange = useCallback(async (newStatus: string) => {
    setContactStatus(newStatus);
    if (savedLead?.id) {
      setUpdatingLead(true);
      try {
        const updated = await LeadService.updateLead(savedLead.id, { status: newStatus as LeadStatus });
        setSavedLead(updated);
      } catch (err) {
        console.error("Update status error:", err);
      } finally {
        setUpdatingLead(false);
      }
    }
  }, [savedLead]);

  const handleMethodChange = useCallback(async (newMethod: string) => {
    setContactMethod(newMethod);
    if (savedLead?.id) {
      setUpdatingLead(true);
      try {
        const updated = await LeadService.updateLead(savedLead.id, { contactMethod: newMethod });
        setSavedLead(updated);
      } catch (err) {
        console.error("Update method error:", err);
      } finally {
        setUpdatingLead(false);
      }
    }
  }, [savedLead]);

  // ========== RENDER ==========
  return (
    <div className={classes.AdminOnboardingWrapper}>
      {/* Top Header Tab selector */}
      <div className={classes.OnboardingTabsHeader}>
        <button
          className={`${classes.OnboardingTabBtn} ${onbTab === "haku" ? classes.OnboardingTabBtnActive : ""}`}
          onClick={() => setOnbTab("haku")}
        >
          <FontAwesomeIcon icon={faSearch} /> Manuaalinen Onboarding (YTJ / Places)
        </button>
        <button
          className={`${classes.OnboardingTabBtn} ${onbTab === "pipelines" ? classes.OnboardingTabBtnActive : ""}`}
          onClick={() => setOnbTab("pipelines")}
        >
          <FontAwesomeIcon icon={faRocket} /> Agenttihaut & Tehotehtävät (Pipelines)
        </button>
        <button
          className={`${classes.OnboardingTabBtn} ${onbTab === "kb" ? classes.OnboardingTabBtnActive : ""}`}
          onClick={() => setOnbTab("kb")}
        >
          <FontAwesomeIcon icon={faBookOpen} /> Palvelukohtaiset Tietoalustat (KB)
        </button>
      </div>

      {onbTab === "haku" && (
        <div className={classes.OnboardingLayout}>
      {/* ======== COLUMN 1: Search ======== */}
      <div className={classes.SearchColumn}>
        {/* Mode selector */}
        <div className={classes.ModeSelector}>
          <button
            className={`${classes.ModeBtn} ${searchMode === "places" ? classes.ModeBtnActive : ""}`}
            onClick={() => switchMode("places")}
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} /> Google Places
          </button>
          <button
            className={`${classes.ModeBtn} ${searchMode === "ytjTol" ? classes.ModeBtnActive : ""}`}
            onClick={() => switchMode("ytjTol")}
          >
            <FontAwesomeIcon icon={faBuilding} /> YTJ Toimiala
          </button>
        </div>

        {/* ---- PLACES SEARCH ---- */}
        {searchMode === "places" && (
          <>
            <div className={classes.SearchHeader}>
              <h3>
                <FontAwesomeIcon icon={faSearch} /> Haku
                <div className={classes.TooltipContainer}>
                  <FontAwesomeIcon icon={faInfoCircle} className={classes.InfoIcon} />
                  <span className={classes.TooltipText}>
                    Hae yrityksen nimellä, toimialalla tai osoitteella. Haku toimii parhaiten, kun lisäät mukaan kaupungin (esim. "Putkimies Helsinki").
                    Haku ymmärtää luonnollista kieltä ja hakee laajasti Googlen tietokannasta. Haku on rajattu Suomeen.
                  </span>
                </div>
              </h3>
              <div className={classes.SearchFields}>
                <input
                  type="text"
                  className={classes.SearchInput}
                  placeholder="Yrityksen nimi tai ala"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  id="onboarding-search-input"
                />
                <input
                  type="text"
                  className={classes.SearchInputCity}
                  placeholder="Kaupunki"
                  value={searchCity}
                  onChange={(e) => handleCityChange(e.target.value)}
                  id="onboarding-city-input"
                />
              </div>
            </div>

            <div className={classes.ScrollArea}>
              {searching && (
                <div className={classes.Spinner}>
                  <FontAwesomeIcon icon={faSpinner} spin className={classes.SpinnerIcon} />
                  Haetaan...
                </div>
              )}

              {searchError && <div className={classes.ErrorMsg}>{searchError}</div>}

              <ul className={classes.ResultsList}>
                {searchResults.map((result) => (
                  <li
                    key={result.placeId}
                    className={`${classes.ResultItem} ${selectedPlaceId === result.placeId ? classes.ResultItemActive : ""}`}
                    onClick={() => handleSelectPlace(result.placeId)}
                    id={`result-${result.placeId}`}
                  >
                    <span className={classes.ResultName}>{result.name}</span>
                    <span className={classes.ResultCity}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 3, fontSize: "0.65rem" }} />
                      {result.city || result.address}
                    </span>
                  </li>
                ))}
              </ul>

              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className={classes.EmptyState}>
                  <p>Ei tuloksia</p>
                </div>
              )}

              {!searching && searchQuery.length < 2 && searchResults.length === 0 && (
                <div className={classes.EmptyState}>
                  <div className={classes.EmptyIcon}>
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <p>Syötä hakutermi löytääksesi yrityksiä Google Places -haulla</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* ---- YTJ TOIMIALA SEARCH ---- */}
        {searchMode === "ytjTol" && (
          <>
            <form className={classes.SearchHeader} onSubmit={(e) => { e.preventDefault(); handleTolSearch(1); }}>
              <h3>
                <FontAwesomeIcon icon={faBuilding} /> YTJ Toimialahaku
                <div className={classes.TooltipContainer}>
                  <FontAwesomeIcon icon={faInfoCircle} className={classes.InfoIcon} />
                  <span className={classes.TooltipText}>
                    Käytä Toimialahakua kun haluat ladata suuria määriä yrityksiä (esim. kaikki metsänhoitoyritykset).
                    Voit suodattaa tuloksia hakusanalla (esim. &quot;arboristi&quot;) etsiäksesi yrityksiä nimen perusteella tietyn toimialan sisältä.
                    Tämä haku käyttää virallista YTJ-rekisteriä.
                  </span>
                </div>
              </h3>
              <div style={{ marginTop: "0.5rem" }}>
                <input
                  type="text"
                  className={classes.SearchInput}
                  value={tolNameQuery}
                  onChange={(e) => setTolNameQuery(e.target.value)}
                  placeholder="Yrityksen nimi tai hakusana..."
                />
              </div>
              <div style={{ marginTop: "0.5rem" }}>
                <Select
                  inputName="tolCode"
                  value={selectedTolCode}
                  onChange={(val) => setSelectedTolCode(val as string)}
                  options={tolOptions}
                  placeholder="Toimiala tai koodi..."
                  isSearchable
                  isClearable
                  isCreatable
                />
              </div>
              <div style={{ marginTop: "0.5rem" }}>
                <Select
                  inputName="tolLocation"
                  value={selectedLocation}
                  onChange={(val) => setSelectedLocation(val as string)}
                  options={cityOptions}
                  placeholder="Postitoimipaikka (valinnainen)"
                  isSearchable
                  isClearable
                  isCreatable
                />
              </div>
              <div style={{ marginTop: "0.5rem" }}>
                <Select
                  inputName="tolPostCode"
                  value={selectedPostCode}
                  onChange={(val) => setSelectedPostCode(val as string)}
                  options={postCodeOptions}
                  placeholder="Postinumero (valinnainen)"
                  isSearchable
                  isClearable
                  isCreatable
                />
              </div>
              <button
                type="submit"
                className={classes.TolSearchBtn}
                disabled={!selectedTolCode || tolSearching}
              >
                {tolSearching ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSearch} />}
                {" "}Hae
              </button>
              <button
                type="button"
                className={classes.TolSearchBtn}
                style={{ background: '#6366f1' }}
                disabled={!selectedTolCode || batchStarting}
                onClick={handleStartBatch}
                title="Hae kaikki yritykset taustalla ja tallenna liideinä"
              >
                {batchStarting ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faRocket} />}
                {" "}Tallenna kaikki liideinä
              </button>
            </form>

            <div className={classes.ScrollArea}>
              {tolSearching && (
                <div className={classes.Spinner}>
                  <FontAwesomeIcon icon={faSpinner} spin className={classes.SpinnerIcon} />
                  Haetaan YTJ:stä...
                </div>
              )}

              {tolError && <div className={classes.ErrorMsg}>{tolError}</div>}

              {tolTotalResults > 0 && (
                <div className={classes.TolResultsInfo} style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  {tolTotalResults} tulosta yhteensä
                  {tolTotalResults > 100 && ` · sivu ${tolPage}/${Math.ceil(tolTotalResults / 100)}`}
                </div>
              )}

              {/* Pagination (TOP) */}
              {tolTotalResults > 100 && (
                <div className={classes.Pagination}>
                  <button disabled={tolPage <= 1} onClick={() => handleTolSearch(tolPage - 1)}>←</button>
                  <span>Sivu {tolPage} / {Math.ceil(tolTotalResults / 100)}</span>
                  <button disabled={tolPage >= Math.ceil(tolTotalResults / 100)} onClick={() => handleTolSearch(tolPage + 1)}>→</button>
                </div>
              )}

              {tolResults.length > 0 && (
                <div className={classes.ResultFilterBar} style={{ position: 'sticky', top: 31, zIndex: 2 }}>
                  <input
                    type="text"
                    className={classes.ResultFilterInput}
                    placeholder="Suodata tuloksia..."
                    value={tolResultFilter}
                    onChange={(e) => setTolResultFilter(e.target.value)}
                  />
                </div>
              )}

              <ul className={classes.ResultsList}>
                {tolResults
                  .filter(r => {
                    if (!tolResultFilter.trim()) return true;
                    const q = tolResultFilter.trim().toLowerCase();
                    return r.name.toLowerCase().includes(q) ||
                      r.businessId.includes(q) ||
                      (r.city && r.city.toLowerCase().includes(q)) ||
                      (r.businessLineName && r.businessLineName.toLowerCase().includes(q));
                  })
                  .map((result) => (
                  <li
                    key={result.businessId}
                    className={`${classes.ResultItem} ${selectedYtjId === result.businessId ? classes.ResultItemActive : ""}`}
                    onClick={() => {
                      handleSelectYtjFromTol(result);
                    }}
                    id={`tol-result-${result.businessId}`}
                  >
                    <span className={classes.ResultName}>{result.name}</span>
                    <span className={classes.ResultCity}>
                      {result.city && (
                        <><FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 3, fontSize: "0.65rem" }} />{result.city}</>  
                      )}
                      {result.businessLineName && (
                        <span style={{ marginLeft: result.city ? 8 : 0, color: "#6366f1" }}>{result.businessLineName}</span>
                      )}
                      {result.isCeased && (
                        <span className={classes.TmiBadge} style={{ background: "#fff7ed", color: "#9a3412", marginLeft: 8 }}>
                          Toiminta lakannut
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Pagination (BOTTOM) */}
              {tolTotalResults > 100 && (
                <div className={classes.Pagination}>
                  <button disabled={tolPage <= 1} onClick={() => handleTolSearch(tolPage - 1)}>← Edellinen</button>
                  <span>Sivu {tolPage} / {Math.ceil(tolTotalResults / 100)}</span>
                  <button disabled={tolPage >= Math.ceil(tolTotalResults / 100)} onClick={() => handleTolSearch(tolPage + 1)}>Seuraava →</button>
                </div>
              )}

              {!tolSearching && tolResults.length === 0 && tolTotalResults === 0 && !selectedTolCode && (
                <div className={classes.EmptyState}>
                  <div className={classes.EmptyIcon}>
                    <FontAwesomeIcon icon={faBuilding} />
                  </div>
                  <p>Valitse toimiala ylläolevasta valikosta ja paina Hae</p>
                </div>
              )}

              {/* Batch error */}
              {batchError && <div className={classes.ErrorMsg}>{batchError}</div>}

              {/* Outreach Batches list */}
              {outreachBatches.length > 0 && (
                <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px', padding: '12px' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faListAlt} /> Taustahaut
                  </h4>
                  {outreachBatches.map(batch => (
                    <div
                      key={batch.id}
                      style={{
                        padding: '10px 14px',
                        marginBottom: '6px',
                        borderRadius: '6px',
                        background: batch.status === 'IN_PROGRESS' ? '#fefce8' :
                                    batch.status === 'COMPLETED' ? '#f0fdf4' :
                                    batch.status === 'FAILED' ? '#fef2f2' :
                                    '#f8fafc',
                        border: `1px solid ${
                          batch.status === 'IN_PROGRESS' ? '#fde68a' :
                          batch.status === 'COMPLETED' ? '#bbf7d0' :
                          batch.status === 'FAILED' ? '#fecaca' :
                          '#e2e8f0'
                        }`,
                        fontSize: '0.8rem',
                        cursor: batch.status === 'COMPLETED' && batch.totalSaved > 0 ? 'pointer' : 'default',
                      }}
                      onClick={async () => {
                        if (batch.status !== 'COMPLETED' || batch.totalSaved === 0) return;
                        setExpandedBatchId(batch.id);
                        setBatchLeadsLoading(true);
                        setBatchLeads([]);
                        try {
                          const leads = await OnboardingService.getOutreachBatchLeads(batch.id);
                          setBatchLeads(leads);
                        } catch (err) {
                          console.error('Failed to load batch leads', err);
                          setBatchLeads([]);
                        } finally {
                          setBatchLeadsLoading(false);
                        }
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {batch.mainBusinessLineName || batch.mainBusinessLine}
                          {batch.city && <span style={{ color: '#64748b', fontWeight: 400 }}> · {batch.city}</span>}
                          {batch.postCode && <span style={{ color: '#64748b', fontWeight: 400 }}> · {batch.postCode}</span>}
                        </span>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          marginLeft: '10px',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          background: batch.status === 'IN_PROGRESS' ? '#fef08a' :
                                      batch.status === 'COMPLETED' ? '#86efac' :
                                      batch.status === 'FAILED' ? '#fca5a5' : '#e2e8f0',
                          color: batch.status === 'IN_PROGRESS' ? '#713f12' :
                                 batch.status === 'COMPLETED' ? '#14532d' :
                                 batch.status === 'FAILED' ? '#7f1d1d' : '#475569',
                        }}>
                          {batch.status === 'PENDING' && '⏳ Jonossa'}
                          {batch.status === 'IN_PROGRESS' && (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Käynnissä (s.{batch.pages})</>
                          )}
                          {batch.status === 'COMPLETED' && '✓ Valmis'}
                          {batch.status === 'FAILED' && '✗ Virhe'}
                        </span>
                      </div>
                      {(batch.status === 'IN_PROGRESS' || batch.status === 'COMPLETED') && (
                        <div style={{ marginTop: '5px', color: '#475569', fontSize: '0.75rem' }}>
                          Löydetty: {batch.totalFound} · Tallennettu: <strong style={{ color: '#16a34a' }}>{batch.totalSaved}</strong> · Duplikaatit: {batch.totalDuplicate} · Virheet: {batch.totalError}
                        </div>
                      )}
                      {batch.status === 'FAILED' && batch.errorMessage && (
                        <div style={{ marginTop: '5px', color: '#dc2626', fontSize: '0.75rem' }}>{batch.errorMessage}</div>
                      )}
                      {batch.status === 'COMPLETED' && batch.totalSaved > 0 && (
                        <div style={{ marginTop: '4px', fontSize: '0.7rem', color: '#6366f1' }}>
                          Klikkaa nähdäksesi liidit →
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ======== COLUMN 2: Details ======== */}
      <div className={classes.DetailsColumn}>
        {!selectedPlaceId && !selectedYtjId ? (
          <div className={classes.EmptyState} style={{ marginTop: "80px" }}>
            <div className={classes.EmptyIcon}>
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <p>Valitse yritys vasemmalta nähdäksesi tiedot</p>
          </div>
        ) : initialLoading ? (
          <div className={classes.Spinner} style={{ marginTop: "80px" }}>
            <FontAwesomeIcon icon={faSpinner} spin className={classes.SpinnerIcon} />
            Ladataan yritystietoja...
          </div>
        ) : (
          <>
            {/* Google Places section */}
            <div className={classes.DetailsSection}>
              <h4 className={classes.SectionTitle}>
                <span className={classes.SectionIcon}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                </span>
                Google Places
              </h4>

              {loadingPlace ? (
                <div className={classes.Spinner}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Ladataan tietoja...
                </div>
              ) : placeDetails ? (
                <>
                  <div className={classes.InfoGrid}>
                    <span className={classes.InfoLabel}>Nimi</span>
                    <span className={classes.InfoValue} style={{ fontWeight: 600 }}>{placeDetails.name}</span>

                    <span className={classes.InfoLabel}>Osoite</span>
                    <span className={classes.InfoValue}>{placeDetails.address}</span>

                    {placeDetails.phone && (
                      <>
                        <span className={classes.InfoLabel}>Puhelin</span>
                        <span className={classes.InfoValue}>
                          <a href={`tel:${placeDetails.phone}`}>{placeDetails.phone}</a>
                        </span>
                      </>
                    )}

                    {placeDetails.website && (
                      <>
                        <span className={classes.InfoLabel}>Verkkosivu</span>
                        <span className={classes.InfoValue}>
                          <a href={placeDetails.website} target="_blank" rel="noopener noreferrer">
                            {placeDetails.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                          </a>
                        </span>
                      </>
                    )}

                    {placeDetails.primaryType && (
                      <>
                        <span className={classes.InfoLabel}>Tyyppi</span>
                        <span className={classes.InfoValue}>{placeDetails.primaryType}</span>
                      </>
                    )}

                    {placeDetails.businessStatus && (
                      <>
                        <span className={classes.InfoLabel}>Status</span>
                        <span className={classes.InfoValue}>{placeDetails.businessStatus}</span>
                      </>
                    )}
                  </div>

                  {placeDetails.rating != null && placeDetails.rating > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <span className={classes.RatingBadge}>
                        <FontAwesomeIcon icon={faStar} />
                        {placeDetails.rating} ({placeDetails.userRatingCount} arvostelua)
                      </span>
                    </div>
                  )}

                  {placeDetails.editorialSummary && (
                    <p style={{ fontSize: "0.82rem", color: "#475569", marginTop: 10, lineHeight: 1.5 }}>
                      {placeDetails.editorialSummary}
                    </p>
                  )}

                  {placeDetails.googleMapsUrl && (
                    <div style={{ marginTop: 8 }}>
                      <a
                        href={placeDetails.googleMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: "0.78rem", color: "#2563eb" }}
                      >
                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4 }} />
                        Avaa Google Mapsissa
                      </a>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Vertex AI enrichment section */}
            <div className={classes.DetailsSection}>
              <h4 className={classes.SectionTitle}>
                <span className={classes.SectionIcon}>
                  <FontAwesomeIcon icon={faMagic} />
                </span>
                Vertex AI — Yritystiedot verkosta
                <button 
                  className={classes.CopyBtn} 
                  style={{ marginLeft: 'auto', padding: '2px 6px', marginTop: 0 }}
                  onClick={() => handleEnrich(true)}
                  disabled={loadingEnrichment}
                  title="Hae tiedot uudelleen verkosta"
                >
                  <FontAwesomeIcon icon={faSync} spin={loadingEnrichment} />
                </button>
              </h4>

              {loadingEnrichment ? (
                <div className={classes.Spinner}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Haetaan yritystietoja verkosta (2-5s)...
                </div>
              ) : enrichment ? (
                <div className={classes.AiCard}>
                  {enrichment.data ? (
                    <div className={classes.InfoGrid}>
                      {enrichment.data.email && (
                        <>
                          <span className={classes.InfoLabel}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 4 }} />
                            Sähköposti
                          </span>
                          <span className={classes.InfoValue}>
                            <a href={`mailto:${enrichment.data.email}`}>{enrichment.data.email}</a>
                          </span>
                        </>
                      )}
                      {enrichment.data.phone && (
                        <>
                          <span className={classes.InfoLabel}>
                            <FontAwesomeIcon icon={faPhone} style={{ marginRight: 4 }} />
                            Puhelin
                          </span>
                          <span className={classes.InfoValue}>
                            <a href={`tel:${enrichment.data.phone}`}>{enrichment.data.phone}</a>
                          </span>
                        </>
                      )}
                      {enrichment.data.website && (
                        <>
                          <span className={classes.InfoLabel}>
                            <FontAwesomeIcon icon={faGlobe} style={{ marginRight: 4 }} />
                            Verkkosivu
                          </span>
                          <span className={classes.InfoValue}>
                            <a href={enrichment.data.website} target="_blank" rel="noopener noreferrer">
                              {enrichment.data.website.replace(/^https?:\/\//, "")}
                            </a>
                          </span>
                        </>
                      )}
                      {enrichment.data.contactPerson && (
                        <>
                          <span className={classes.InfoLabel}>Yhteyshenkilö</span>
                          <span className={classes.InfoValue}>{enrichment.data.contactPerson}</span>
                        </>
                      )}
                      {enrichment.data.businessId && (
                        <>
                          <span className={classes.InfoLabel}>Y-tunnus (AI)</span>
                          <span className={classes.InfoValue}>
                            <a 
                              href={`https://tietopalvelu.ytj.fi/yritys/${enrichment.data.businessId}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              {enrichment.data.businessId}
                              <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: 6, fontSize: '0.7rem', color: '#94a3b8' }} />
                            </a>
                          </span>
                        </>
                      )}
                      {enrichment.data.description && (
                        <>
                          <span className={classes.InfoLabel}>Kuvaus</span>
                          <span className={classes.InfoValue}>{enrichment.data.description}</span>
                        </>
                      )}
                    </div>
                  ) : enrichment.rawText ? (
                    <p style={{ fontSize: "0.82rem", color: "#334155", whiteSpace: "pre-wrap" }}>{enrichment.rawText}</p>
                  ) : null}

                  {/* Social links */}
                  {enrichment.data?.socialLinks && (
                    <div className={classes.SocialLinks}>
                      {enrichment.data.socialLinks.facebook && (
                        <a href={enrichment.data.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className={classes.SocialLink}>
                          Facebook
                        </a>
                      )}
                      {enrichment.data.socialLinks.instagram && (
                        <a href={enrichment.data.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className={classes.SocialLink}>
                          Instagram
                        </a>
                      )}
                      {enrichment.data.socialLinks.linkedin && (
                        <a href={enrichment.data.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className={classes.SocialLink}>
                          LinkedIn
                        </a>
                      )}
                    </div>
                  )}

                  {/* Sources */}
                  {enrichment.sources && enrichment.sources.length > 0 && (
                    <ul className={classes.AiSourcesList}>
                      {enrichment.sources.map((s, i) => (
                        <li key={i}>
                          <a href={s.url} target="_blank" rel="noopener noreferrer">
                            {s.title || s.url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : selectedPlaceId ? (
                <div className={classes.EmptyState} style={{ padding: "16px" }}>
                  <p>Ei rikastettuja tietoja saatavilla</p>
                </div>
              ) : null}
            </div>

            {/* YTJ section */}
            <div className={classes.DetailsSection}>
              <h4 className={classes.SectionTitle}>
                <span className={classes.SectionIcon}>
                  <FontAwesomeIcon icon={faIdCard} />
                </span>
                YTJ — Yritysrekisteri
              </h4>

              {/* Manual Y-tunnus input */}
              <div className={classes.YtjManualInput}>
                <input
                  type="text"
                  className={classes.YtjInput}
                  placeholder="Y-tunnus tai yrityksen nimi"
                  value={ytjManualInput}
                  onChange={(e) => setYtjManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleManualYtjSearch()}
                  id="ytj-manual-input"
                />
                <button
                  className={classes.YtjSearchBtn}
                  onClick={handleManualYtjSearch}
                  disabled={loadingYtj || !ytjManualInput.trim()}
                  id="ytj-search-btn"
                >
                  {loadingYtj ? <FontAwesomeIcon icon={faSpinner} spin /> : "Hae"}
                </button>
              </div>

              {loadingYtj && (
                <div className={classes.Spinner}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Haetaan YTJ:stä...
                </div>
              )}

              {/* YTJ search results (if multiple) */}
              {ytjResults.length > 1 && !ytjDetails && (
                <div className={classes.YtjResults}>
                  <p style={{ fontSize: "0.78rem", color: "#64748b", margin: "8px 0 4px" }}>
                    Löytyi {ytjResults.length} tulosta — valitse oikea:
                  </p>
                  {ytjResults.map((r) => (
                    <div
                      key={r.businessId}
                      className={`${classes.YtjResultItem} ${selectedYtjId === r.businessId ? classes.YtjResultItemActive : ""}`}
                      onClick={() => handleSelectYtj(r.businessId)}
                    >
                      <strong>{r.name}</strong>
                      <span style={{ color: "#64748b", marginLeft: 6, fontSize: "0.78rem" }}>{r.businessId}</span>
                      {r.isToiminimi ? (
                        <span className={classes.TmiBadge}>Tmi</span>
                      ) : (
                        <span className={classes.OyBadge}>{r.companyForm}</span>
                      )}
                      {r.isCeased && (
                        <span className={classes.TmiBadge} style={{ background: "#fff7ed", color: "#9a3412" }}>
                          Toiminta lakannut
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* YTJ details */}
              {ytjDetails && (
                <div style={{ marginTop: 10 }}>
                  {ytjDetails.isCeased && (
                    <div className={classes.TmiWarning} style={{ marginBottom: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412" }}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <div style={{ fontWeight: 600 }}>Toiminta lakannut</div>
                      {ytjDetails.terminationDate && <div style={{ fontSize: "0.7rem", opacity: 0.8 }}>Päättymispäivä: {ytjDetails.terminationDate}</div>}
                    </div>
                  )}
                  <div className={classes.InfoGrid}>
                    <span className={classes.InfoLabel}>Y-tunnus</span>
                    <span className={classes.InfoValue} style={{ fontWeight: 600 }}>
                      {ytjDetails.businessId}
                      <a
                        href={`https://tietopalvelu.ytj.fi/yritys/${ytjDetails.businessId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginLeft: 8, color: "#64748b" }}
                        title="Avaa YTJ-tietopalvelussa"
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "0.75rem" }} />
                      </a>
                    </span>

                    <span className={classes.InfoLabel}>Nimi</span>
                    <span className={classes.InfoValue}>{ytjDetails.name}</span>

                    <span className={classes.InfoLabel}>Muoto</span>
                    <span className={classes.InfoValue}>
                      {ytjDetails.companyForm}
                      {ytjDetails.isToiminimi ? (
                        <span className={classes.TmiBadge}>Toiminimi</span>
                      ) : (
                        <span className={classes.OyBadge}>{ytjDetails.companyForm}</span>
                      )}
                    </span>

                    {ytjDetails.businessLines && ytjDetails.businessLines.length > 0 && (
                      <>
                        <span className={classes.InfoLabel}>Toimiala</span>
                        <span className={classes.InfoValue}>
                          {ytjDetails.businessLines.map((bl) => bl.name).join(", ")}
                        </span>
                      </>
                    )}

                    {ytjDetails.addresses && ytjDetails.addresses.length > 0 && (
                      <>
                        <span className={classes.InfoLabel}>Osoite</span>
                        <span className={classes.InfoValue}>
                          {ytjDetails.addresses[0].street}, {ytjDetails.addresses[0].postCode} {ytjDetails.addresses[0].city}
                        </span>
                      </>
                    )}

                    {ytjDetails.phone && (
                      <>
                        <span className={classes.InfoLabel}>Puhelin</span>
                        <span className={classes.InfoValue}>
                          <a href={`tel:${ytjDetails.phone}`}>{ytjDetails.phone}</a>
                        </span>
                      </>
                    )}

                    {ytjDetails.website && (
                      <>
                        <span className={classes.InfoLabel}>Verkkosivu</span>
                        <span className={classes.InfoValue}>
                          <a href={ytjDetails.website.startsWith("http") ? ytjDetails.website : `https://${ytjDetails.website}`} target="_blank" rel="noopener noreferrer">
                            {ytjDetails.website}
                          </a>
                        </span>
                      </>
                    )}
                  </div>

                </div>
              )}

              {!loadingYtj && ytjResults.length === 0 && !ytjDetails && selectedPlaceId && (
                <p style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: 8 }}>
                  Automaattihaku ei löytänyt — syötä Y-tunnus manuaalisesti
                </p>
              )}

              {/* Pikalinkit muualle - Shown if we have ANY business ID (YTJ or AI) */}
              {(ytjDetails?.businessId || enrichment?.data?.businessId) && (
                <div style={{ marginTop: 20, paddingTop: 15, borderTop: '1px solid #f1f5f9' }}>
                  <div className={classes.SectionTitle} style={{ fontSize: '0.7rem' }}>
                    Pikalinkit muualle
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
                    <a
                      href={`https://www.finder.fi/search?what=${ytjDetails?.businessId || enrichment?.data?.businessId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.SocialLink}
                      style={{ background: '#eff6ff', color: '#1e40af', padding: '4px 8px' }}
                    >
                      Finder
                    </a>
                    <a
                      href={`https://www.asiakastieto.fi/yritykset/haku?q=${ytjDetails?.businessId || enrichment?.data?.businessId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.SocialLink}
                      style={{ background: '#f8fafc', color: '#334155', padding: '4px 8px' }}
                    >
                      Asiakastieto
                    </a>
                    <a
                      href={`https://www.proff.fi/toimialahaku?q=${ytjDetails?.businessId || enrichment?.data?.businessId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.SocialLink}
                      style={{ background: '#fefce8', color: '#854d0e', padding: '4px 8px' }}
                    >
                      Proff
                    </a>
                    <a
                      href={`https://www.kauppalehti.fi/yritykset/yritys/${ytjDetails?.businessId || enrichment?.data?.businessId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.SocialLink}
                      style={{ background: '#fff7ed', color: '#9a3412', padding: '4px 8px' }}
                    >
                      Kauppalehti
                    </a>
                    <a
                      href={`https://www.google.com/search?q=${encodeURIComponent(ytjDetails?.name || selectedPlaceName || "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.SocialLink}
                      style={{ background: '#f0fdf4', color: '#166534', padding: '4px 8px' }}
                    >
                      <FontAwesomeIcon icon={faSearch} style={{ marginRight: 4 }} />
                      Google
                    </a>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ======== COLUMN 3: Contact ======== */}
      <div className={classes.ContactColumn}>
        {!selectedPlaceId && !selectedYtjId ? (
          <div className={classes.EmptyState} style={{ marginTop: "80px" }}>
            <div className={classes.EmptyIcon}>
              <FontAwesomeIcon icon={faPhoneAlt} />
            </div>
            <p>Valitse yritys aloittaaksesi kontaktoinnin</p>
          </div>
        ) : initialLoading ? (
          <div className={classes.Spinner} style={{ marginTop: "80px" }}>
            <FontAwesomeIcon icon={faSpinner} spin className={classes.SpinnerIcon} />
            Tarkistetaan...
          </div>
        ) : (
          <>
            {/* Contact status */}
            <div className={classes.ContactSection}>

              {/* Lead status badge - show only one */}
              {duplicateLead && (
                <div style={{ background: "#e0f2fe", border: "1px solid #7dd3fc", borderRadius: 8, padding: "0.75rem", marginBottom: "1rem" }}>
                  <strong style={{ color: "#0369a1" }}>📋 Liidi jo luotu — muutokset päivittyvät automaattisesti</strong>
                  <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem", color: "#0c4a6e" }}>
                    {(savedLead?.ytjName || savedLead?.placeName || duplicateLead.ytjName || duplicateLead.placeName)} ({LEAD_STATUS_LABELS[(savedLead?.status || duplicateLead.status) as LeadStatus] || savedLead?.status || duplicateLead.status})
                  </p>
                </div>
              )}
              {savedLead && !duplicateLead && (
                <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.75rem", marginBottom: "1rem" }}>
                  <strong style={{ color: "#166534" }}>✓ Liidi tallennettu — muutokset päivittyvät automaattisesti</strong>
                </div>
              )}
              {savingLead && !savedLead && (
                <div style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem", marginBottom: "1rem" }}>
                  <strong style={{ color: "#92400e" }}><FontAwesomeIcon icon={faSpinner} spin /> Tallennetaan liidiä...</strong>
                </div>
              )}
              {checkingDuplicate && !savedLead && (
                <div style={{ background: "#f5f3ff", border: "1px solid #c4b5fd", borderRadius: 8, padding: "0.75rem", marginBottom: "1rem" }}>
                  <strong style={{ color: "#5b21b6" }}><FontAwesomeIcon icon={faSpinner} spin /> Tarkistetaan duplikaattia...</strong>
                </div>
              )}
              {!savedLead && !duplicateLead && !savingLead && !checkingDuplicate && (selectedPlaceId || selectedYtjId) && (
                <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "0.75rem", marginBottom: "1rem" }}>
                  <strong style={{ color: "#991b1b" }}>⚠ Liidiä ei tallennettu</strong>
                  <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem", color: "#7f1d1d" }}>
                    {!ytjDetails && !loadingYtj && !enrichment?.data?.businessId && "YTJ-tietoja ei löytynyt — liidi tallennetaan vain kun YTJ/PRH-matchaus onnistuu tai Y-tunnus tunnistetaan verkosta."}
                    {!ytjDetails && !loadingYtj && enrichment?.data?.businessId && (
                      <>
                        <span style={{ display: 'block', marginBottom: 4 }}>
                          Official YTJ register data is not available for this company type (Sole proprietorship/Tmi) via the open API.
                        </span>
                        <span>
                          <strong>✓ AI found Business ID: {enrichment.data.businessId}</strong>. Lead will be saved using AI and Google Places data.
                        </span>
                      </>
                    )}
                    {!ytjDetails && loadingYtj && "Haetaan YTJ-tietoja..."}
                    {ytjDetails && "Tallennusvirhe — yritä valita yritys uudelleen."}
                  </p>
                </div>
              )}

              {isToiminimi && (
                <div style={{ background: "#fff7ed", border: "1px solid #fdba74", borderRadius: 8, padding: "0.75rem", marginBottom: "1.5rem" }}>
                  <strong style={{ color: "#9a3412" }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6 }} />
                    Yksityinen elinkeinonharjoittaja (Tmi)
                  </strong>
                  <p style={{ margin: "0.25rem 0 0", fontSize: "0.82rem", color: "#7c2d12", lineHeight: 1.4 }}>
                    Laki kieltää suoramarkkinoinnin sähköpostitse ilman ennakkosuostumusta. 
                    <strong> Käytä puhelinta kontaktointiin.</strong>
                  </p>
                </div>
              )}

              <h4 className={classes.SectionTitle}>Kontaktoinnin status {updatingLead && <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "0.7rem", marginLeft: 6 }} />}</h4>
              <div className={classes.StatusButtons}>
                {(["NOT_CONTACTED", "CONTACTED", "NOT_INTERESTED", "CONTRACT"] as const).map((status) => (
                  <button
                    key={status}
                    className={`${classes.StatusBtn} ${contactStatus === status ? classes.StatusBtnActive : ""}`}
                    onClick={() => handleStatusChange(status)}
                    id={`status-${status}`}
                  >
                    {LEAD_STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact method */}
            <div className={classes.ContactSection}>
              <h4 className={classes.SectionTitle}>Kontaktointitapa</h4>
              <div className={classes.StatusButtons}>
                <button
                  className={`${classes.MethodBtn} ${contactMethod === "PHONE" || contactMethod === "PUHELU" ? classes.MethodBtnActive : ""}`}
                  onClick={() => handleMethodChange("PHONE")}
                  id="method-puhelu"
                >
                  <FontAwesomeIcon icon={faPhoneAlt} style={{ marginRight: 4 }} />
                  Puhelu
                </button>
                <button
                  className={`${isToiminimi ? classes.MethodBtnDisabled : classes.MethodBtn} ${
                    contactMethod === "EMAIL" && !isToiminimi ? classes.MethodBtnActive : ""
                  }`}
                  onClick={() => !isToiminimi && handleMethodChange("EMAIL")}
                  disabled={isToiminimi}
                  id="method-sahkoposti"
                >
                  <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 4 }} />
                  Sähköposti
                </button>
              </div>

              {isToiminimi && (
                <div className={classes.TmiWarning}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  Toiminimelle (Tmi) ei voi lähettää markkinointisähköpostia. Käytä puhelinkontaktointia.
                </div>
              )}
            </div>

            {/* Notes (OfferNote-style comment component) */}
            <div className={classes.ContactSection}>
              <h4 className={classes.SectionTitle}>Muistiinpanot</h4>

              {!savedLead?.id ? (
                <div style={{ padding: "1rem", backgroundColor: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1", textAlign: "center", color: "#64748b", fontSize: "0.85rem" }}>
                  Valitse yritys tallentaaksesi muistiinpanoja.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {/* New note composer */}
                  <form onSubmit={handleAddNote} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <textarea
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      onFocus={() => setIsComposerFocused(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (newNoteText.trim() && !savingNote) {
                            handleAddNote(e);
                          }
                        }
                      }}
                      placeholder="Kirjoita uusi muistiinpano... (Paina Enter tallentaaksesi, Shift+Enter rivinvaihtoon)"
                      style={{
                        width: "100%",
                        minHeight: isComposerFocused ? "80px" : "38px",
                        maxHeight: "200px",
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: isComposerFocused ? "1px solid #f97316" : "1px solid #e2e8f0",
                        outline: "none",
                        fontSize: "0.85rem",
                        color: "#334155",
                        backgroundColor: "#ffffff",
                        boxShadow: isComposerFocused ? "0 0 0 2px rgba(249,115,22,0.15)" : "none",
                        transition: "all 0.2s",
                        resize: isComposerFocused ? "vertical" : "none",
                        lineHeight: 1.5,
                      }}
                      id="lead-note-input"
                    />
                    {isComposerFocused && (
                      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                          <button
                            type="submit"
                            disabled={savingNote || !newNoteText.trim()}
                            style={{
                              padding: "5px 12px", fontSize: "0.8rem", fontWeight: 600,
                              backgroundColor: "#f97316", border: "1px solid #ea580c", color: "white",
                              borderRadius: 6, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                              opacity: savingNote || !newNoteText.trim() ? 0.5 : 1,
                            }}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: "0.7rem" }} />
                            {savingNote ? "Tallennetaan..." : "Tallenna"}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setNewNoteText(""); setIsComposerFocused(false); }}
                            style={{ background: "none", border: "none", color: "#64748b", fontSize: "0.8rem", cursor: "pointer", padding: "4px 6px" }}
                          >
                            Peruuta
                          </button>
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>Enter tallentaa · Shift+Enter rivinvaihto</span>
                      </div>
                    )}
                  </form>

                  {/* Notes timeline */}
                  {notesLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "0.75rem" }}>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#94a3b8" }} />
                    </div>
                  ) : notesList.length === 0 ? (
                    <div style={{ padding: "1rem", backgroundColor: "#f8fafc", borderRadius: 8, border: "1px dashed #e2e8f0", textAlign: "center", color: "#94a3b8", fontSize: "0.82rem" }}>
                      Ei muistiinpanoja vielä.
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                      {notesList.map((note) => {
                        const isAuthor = authUser && note.userId === authUser.id?.toString();
                        const initials = getInitials(note.userName);
                        const isEditing = editingNoteId === note.id;

                        return (
                          <div key={note.id} style={{ display: "flex", gap: "0.6rem" }}>
                            {/* Avatar */}
                            <div style={{
                              width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                              background: isAuthor ? "linear-gradient(135deg, #f97316, #ea580c)" : "linear-gradient(135deg, #64748b, #475569)",
                              color: "white", display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: "0.65rem", fontWeight: 700,
                            }}>
                              {initials}
                            </div>
                            {/* Content */}
                            <div style={{ flexGrow: 1, backgroundColor: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px" }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>
                                  {note.userName || "Tuntematon"}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                  <span style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{formatRelativeTime(note.created)}</span>
                                  {isAuthor && !isEditing && (
                                    <>
                                      <button onClick={() => { setEditingNoteId(note.id); setEditingNoteText(note.content); }}
                                        style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "2px", fontSize: "0.7rem" }}
                                        title="Muokkaa"
                                      ><FontAwesomeIcon icon={faEdit} /></button>
                                      {deletingNoteId === note.id ? (
                                        <button onClick={() => handleDeleteNote(note.id)}
                                          disabled={deletingNote}
                                          style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: "2px", fontSize: "0.7rem" }}
                                        ><FontAwesomeIcon icon={faCheck} /></button>
                                      ) : (
                                        <button onClick={() => setDeletingNoteId(note.id)}
                                          style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", padding: "2px", fontSize: "0.7rem" }}
                                          title="Poista"
                                        ><FontAwesomeIcon icon={faTrash} /></button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              {isEditing ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                                  <textarea value={editingNoteText} onChange={(e) => setEditingNoteText(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        if (editingNoteText.trim() && !savingEdit) {
                                          handleEditNote(note.id, editingNoteText);
                                        }
                                      }
                                    }}
                                    style={{ width: "100%", minHeight: 60, padding: "6px 8px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: "0.82rem", resize: "vertical", outline: "none" }}
                                  />
                                  <div style={{ display: "flex", gap: "0.4rem" }}>
                                    <button onClick={() => handleEditNote(note.id, editingNoteText)} disabled={savingEdit}
                                      style={{ padding: "3px 10px", fontSize: "0.75rem", backgroundColor: "#f97316", color: "white", border: "none", borderRadius: 4, cursor: "pointer" }}
                                    >{savingEdit ? "Tallennetaan..." : "Tallenna"}</button>
                                    <button onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }}
                                      style={{ padding: "3px 10px", fontSize: "0.75rem", background: "none", border: "none", color: "#64748b", cursor: "pointer" }}
                                    >Peruuta</button>
                                  </div>
                                </div>
                              ) : (
                                <p style={{ margin: 0, fontSize: "0.82rem", color: "#475569", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                                  {note.content}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Email generation */}
            <div className={classes.ContactSection}>
              <h4 className={classes.SectionTitle}>
                <span className={classes.SectionIcon}>
                  <FontAwesomeIcon icon={faMagic} />
                </span>
                Sähköpostin generointi (AI)
              </h4>

              <button
                className={classes.GenerateEmailBtn}
                onClick={handleGenerateEmail}
                disabled={generatingEmail || isToiminimi || !selectedPlaceName}
                id="generate-email-btn"
              >
                {generatingEmail ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Generoidaan...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} />
                    Generoi sähköposti
                  </>
                )}
              </button>

              {isToiminimi && (
                <p style={{ fontSize: "0.75rem", color: "#991b1b", marginTop: 6 }}>
                  Sähköpostin generointi on estetty toiminimille.
                </p>
              )}

              {generatedEmail && !generatedEmail.error && (
                <div className={classes.EmailPreview}>
                  <div className={classes.EmailSubject}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6, color: "#6366f1" }} />
                    {generatedEmail.subject}
                  </div>
                  <div className={classes.EmailBody}>{generatedEmail.body}</div>
                  <button className={classes.CopyBtn} onClick={handleCopyEmail} id="copy-email-btn">
                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                    {copied ? "Kopioitu!" : "Kopioi leikepöydälle"}
                  </button>
                </div>
              )}

              {generatedEmail?.error && (
                <div className={classes.ErrorMsg}>
                  {generatedEmail.message || "Sähköpostin generointi epäonnistui"}
                </div>
              )}
            </div>


          </>
        )}
      </div>
      </div>
    )}

    {onbTab === "pipelines" && (
      <SupplyPipelineTab 
        onSelectLead={handleSelectLeadFromPipeline} 
        onNavigateToKb={(token) => {
          setActiveKbToken(token);
          setOnbTab("kb");
        }}
      />
    )}

    {onbTab === "kb" && (
      <KnowledgeBaseTab 
        initialServiceToken={activeKbToken} 
        onClearInitialToken={() => setActiveKbToken("")} 
      />
    )}

    {/* ======== BATCH LEADS MODAL ======== */}
    {expandedBatchId && (
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={() => { setExpandedBatchId(null); setBatchLeads([]); }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: '12px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b' }}>
                Taustahaun liidit
              </h3>
              {(() => {
                const batch = outreachBatches.find(b => b.id === expandedBatchId);
                return batch ? (
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '2px' }}>
                    {batch.mainBusinessLineName || batch.mainBusinessLine}
                    {batch.postCode && ` · ${batch.postCode}`}
                    {batch.city && ` · ${batch.city}`}
                    {' · '}<strong style={{ color: '#16a34a' }}>{batch.totalSaved}</strong> liidiä
                  </div>
                ) : null;
              })()}
            </div>
            <button
              onClick={() => { setExpandedBatchId(null); setBatchLeads([]); }}
              style={{
                background: 'none', border: 'none', fontSize: '1.2rem',
                cursor: 'pointer', color: '#94a3b8', padding: '4px 8px',
              }}
            >✕</button>
          </div>

          {/* Content */}
          <div style={{ padding: '0', overflowY: 'auto', flex: 1 }}>
            {batchLeadsLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Ladataan liidejä...
              </div>
            ) : batchLeads.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                Ei liidejä
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Yritys</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Y-tunnus</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Kaupunki</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Muoto</th>
                  </tr>
                </thead>
                <tbody>
                  {batchLeads.map((lead: any) => (
                    <tr
                      key={lead.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        cursor: lead.businessId ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (!lead.businessId) return;
                        setExpandedBatchId(null);
                        setBatchLeads([]);
                        setSelectedPlaceId(null);
                        handleSelectYtj(lead.businessId);
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f0f9ff'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                    >
                      <td style={{ padding: '8px 16px', color: '#1e293b', fontWeight: 500 }}>
                        {lead.ytjName || lead.name || '—'}
                      </td>
                      <td style={{ padding: '8px 16px', color: '#64748b', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {lead.businessId || '—'}
                      </td>
                      <td style={{ padding: '8px 16px', color: '#64748b' }}>
                        {lead.city || '—'}
                      </td>
                      <td style={{ padding: '8px 16px', color: '#64748b' }}>
                        {lead.companyForm || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    )}

    </div>
  );
};

export default Onboarding;
